import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Badge from "aldoo-ra/Badge"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import PermissionCheck from "aldoo-ra/PermissionChecker"
import NewsletterAPI from "../api"
import moment from "moment"
import PubSub from "pubsub-js"
import {
  PlusCircleIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline"
import { getLanguageNameFromCode } from "../constants"
import ExportOptionsDialog from './components/ExportOptionsDialog'
import ImportSubscribersDialog from './components/ImportSubscribersDialog'
import AddSubscriberDialog from './components/AddSubscriberDialog'
import SubscriberController from './controllers/SubscriberController'

export default function NewsletterSubscribers() {
  const [selectedItems, setSelectedItems] = useState([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Dialog states
  const [addDialog, setAddDialog] = useState(false)
  const [importDialog, setImportDialog] = useState(false)
  const [exportDialog, setExportDialog] = useState(false)

  useEffect(() => {
    const token = PubSub.subscribe('newsletter.subscribers.imported', () => {
      setRefreshTrigger(prev => prev + 1)
    })

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1)
    setSelectedItems([])
  }

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return

    const confirmed = await showPromptDialogAsync({
      title: "Delete Subscribers",
      message: `Are you sure you want to delete ${selectedItems.length} subscriber(s)? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    })

    if (!confirmed) return

    try {
      await NewsletterAPI.deleteSubscribers(selectedItems)
      showNotification({
        message: `✅ ${selectedItems.length} subscriber(s) deleted successfully`,
        className: "bg-admin_primary text-white",
      })
      handleRefresh()
    } catch (error) {
      showNotification({
        message: "Error: Failed to delete subscribers",
        className: "bg-admin_error text-white",
      })
    }
  }

  const handleDeleteSubscriber = async (subscriberEmail) => {
    const confirmed = await showPromptDialogAsync({
      title: "Delete Subscriber",
      message: "Are you sure you want to delete this subscriber? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    })

    if (!confirmed) return

    const response = await SubscriberController.deleteSubscribers([subscriberEmail])
    if (response.success) {
      handleRefresh()
    }
  }

  const handleToggleStatus = async (subscriberEmail, currentStatus) => {
    const response = await SubscriberController.updateSubscriber(subscriberEmail, { active: !currentStatus })
    if (response.success) {
      handleRefresh()
    } else {
      let errorTitle = "Error"

      switch (response.error) {
        case "SUBSCRIBER_NOT_FOUND":
          errorTitle = "Subscriber Not Found"
          break
        case "MISSING_EMAIL":
          errorTitle = "Missing Email"
          break
        case "MISSING_DATA":
          errorTitle = "Missing Data"
          break
        case "SERVER_ERROR":
          errorTitle = "Server Error"
          break
        default:
          errorTitle = "Error"
      }

      showNotification({
        message: `${errorTitle}: ${response.message || "Failed to update subscriber status"}`,
        className: "bg-admin_error text-white",
      })
    }
  }

  const handleExport = () => {
    setExportDialog(true)
  }

  const handleExportWithFields = async (selectedFields) => {
    try {
      const blob = await NewsletterAPI.exportSubscribers(selectedFields)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showNotification({
        message: "✅ Subscribers exported successfully",
        className: "bg-admin_primary text-white",
      })
    } catch (error) {
      console.error("Export error:", error)
      showNotification({
        message: `Error: ${error.message || "Failed to export subscribers"}`,
        className: "bg-admin_error text-white",
      })
    }
  }

  const columns = [
    {
      id: "email",
      title: "Email",
      headerClassName: "w-1/3",
      render: (_, item) => (
        <div className="flex flex-col">
          <Tooltip
            display="block"
            content={
              <div className="text-center">
                <Typography
                  variant="admin_txt1"
                  className="block"
                >
                  {item.email}
                </Typography>

                <Typography variant="admin_txt2_dim">
                  Click to copy
                </Typography>
              </div>
            }
          >
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(item.email)
                  showNotification({
                    message: "Copied to clipboard",
                    className: "bg-admin_primary text-white"
                  })
                } catch (error) {
                  console.error("Failed to copy:", error)
                }
              }}
              className="w-full truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
            >
              {item.email}
            </button>
          </Tooltip>

          {(item.name || item.userName) && (
            <Typography
              variant="admin_txt2_dim"
              className="block mt-1"
            >
              {item.name || item.userName}
            </Typography>
          )}
        </div>
      ),
    },
    {
      id: "status",
      title: "Subscribed",
      headerClassName: "w-[96px]",
      render: (_, item) => (
        <ToggleSwitch
          variant="admin"
          isOn={item.active}
          onChange={(checked) => handleToggleStatus(item.email, item.active)}
          disabled={false}
        />
      ),
    },
    {
      id: "health",
      title: (
        <div className="flex items-center space-x-1">
          <span>Health</span>

          <Tooltip
            content={
              <div className="max-w-xs p-4">
                <Typography
                  component="h3"
                  variant="admin_body_accent"
                  className="mb-5"
                >
                  Subscriber Health Status
                </Typography>

                <Typography
                  variant="admin_txt1"
                  className="block mb-4"
                >
                  <strong>Healthy:</strong> Emails are successfully delivered to this subscriber.
                </Typography>

                <Typography
                  variant="admin_txt1"
                  className="block mb-4"
                >
                  <strong>Unhealthy:</strong> 3 or more consecutive email delivery failures. Unhealthy subscribers are excluded from releases when "Include Unhealthy" is set to "No".
                </Typography>

                <Typography variant="admin_txt1_dim">
                  Health status is automatically restored to "Healthy" after any successful email delivery.
                </Typography>
              </div>
            }
            placement="top"
            variant="admin"
          >
            <div className="w-4 h-4 flex items-center justify-center bg-admin_neutral_1 rounded-full cursor-help ml-1">
              <span className="text-xs text-admin_text_secondary">
                ?
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[90px]",
      render: (_, item) => {
        const isHealthy = item.healthy !== false
        return (
          <div className="flex items-center space-x-2">
            <Badge variant={isHealthy ? "admin_success" : "admin_error"}>
              {isHealthy ? "Healthy" : "Unhealthy"}
            </Badge>

            {!isHealthy && item.failedDeliveries && (
              <Typography variant="admin_txt2_dim">
                {item.failedDeliveries} failures
              </Typography>
            )}
          </div>
        )
      },
    },
    {
      id: "type",
      title: (
        <div className="flex items-center space-x-1">
          <span>Type</span>

          <Tooltip
            content={
              <div className="max-w-xs p-4">
                <Typography
                  component="h3"
                  variant="admin_body_accent"
                  className="mb-5"
                >
                  Subscriber Type
                </Typography>

                <Typography
                  variant="admin_txt1"
                  className="block mb-4"
                >
                  <strong>Registered:</strong> Subscriber has a user account in the system and has signed up through the app.
                </Typography>

                <Typography
                  variant="admin_txt1"
                  className="block mb-4"
                >
                  <strong>Anonymous:</strong> Subscriber only provided their email address without creating a full user account.
                </Typography>

                <Typography variant="admin_txt1_dim">
                  Both types receive newsletters, but registered users can be filtered separately in release settings.
                </Typography>
              </div>
            }
            placement="top"
            variant="admin"
          >
            <div className="w-4 h-4 flex items-center justify-center bg-admin_neutral_1 rounded-full cursor-help ml-1">
              <span className="text-xs text-admin_text_secondary">
                ?
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[96px]",
      render: (_, item) => {
        const isRegistered = item.userId || item.isRegistered
        return (
          <Badge variant={isRegistered ? "admin_primary" : "default"}>
            {isRegistered ? "Registered" : "Anonymous"}
          </Badge>
        )
      },
    },
    {
      id: "subscribedAt",
      title: "Added On",
      headerClassName: "w-[86px] whitespace-nowrap",
      render: (_, item) => (
        <Typography variant="admin_txt2_dim">
          {moment(item.subscribedAt).format("MMM DD, YYYY")}
        </Typography>
      ),
    },
    {
      id: "language",
      title: "Language",
      headerClassName: "w-[100px]",
      render: (_, item) => (
        <Typography variant="admin_txt2">
          {getLanguageNameFromCode(item.language || "en")}
        </Typography>
      ),
    },
    {
      id: "unsubscribeReason",
      title: "Unsubscribe Reason",
      headerClassName: "w-[170px] whitespace-nowrap",
      render: (_, item) => {
        if (item.active || !item.unsubscribeReason) {
          return (
            <Typography
              variant="admin_txt2_dim"
              className="italic"
            >
              {item.active ? "Active subscriber" : "-"}
            </Typography>
          )
        }

        return (
          <div>
            <Tooltip
              content={`Unsubscribed: ${item.unsubscribeReason}`}
              placement="top"
              variant="admin"
            >
              <Typography
                variant="admin_txt2"
                className="text-admin_text truncate cursor-help"
              >
                {item.unsubscribeReason}
              </Typography>
            </Tooltip>
          </div>
        )
      },
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-20 text-center",
      render: (_, item) => (
        <div className="flex items-center justify-center">
          <Button
            variant="admin_icon"
            onClick={() => handleDeleteSubscriber(item.email)}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6">
      <PermissionCheck permission="role:newsletter" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Typography
            component="h1"
            variant="admin_h3"
          >
            Newsletter Subscribers
          </Typography>

          <div className="flex items-center space-x-3">
            <Button
              variant="admin_secondary"
              onClick={handleExport}
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Export
            </Button>

            <Button
              variant="admin_secondary"
              onClick={() => setImportDialog(true)}
            >
              <ArrowUpTrayIcon className="w-4 h-4 mr-1" />
              Import
            </Button>

            <Button
              variant="admin_primary"
              onClick={() => setAddDialog(true)}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add
            </Button>
          </div>
        </div>

        <DataTable
          variant="admin_browser"
          preferredHeight="85vh"
          apiMethod={NewsletterAPI.getSubscribers}
          localUserPreferencesKey="admin.newsletter.subscribers"
          renders={columns}
          searchField="email name userName"
          enableSelection={true}
          selectedItems={selectedItems}
          onSelectionChange={(ids) => setSelectedItems(ids)}
          bulkActions={[
            {
              label: "Delete",
              variant: "admin_error",
              icon: <TrashIcon className="w-4 h-4" />,
              onClick: handleBulkDelete,
            }
          ]}
          itemIdField="email"
          itemsPerPage={25}
          refreshKey={refreshTrigger}
          styleOverrides={{
            table: "w-full table-fixed",
            container: "relative w-full flex flex-col h-[85vh] justify-start bg-admin_paper rounded-3xl px-6 pt-12 pb-6",
            searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          }}
        />

        <ExportOptionsDialog
          isOpen={exportDialog}
          onClose={() => setExportDialog(false)}
          onConfirm={handleExportWithFields}
        />
        <ImportSubscribersDialog
          isOpen={importDialog}
          onClose={() => setImportDialog(false)}
          onSuccess={() => handleRefresh()}
        />
        <AddSubscriberDialog
          isOpen={addDialog}
          onClose={() => setAddDialog(false)}
          onSuccess={() => handleRefresh()}
        />
      </div>
    </div>
  )
}
